import type { CountdownTime } from '@/hooks/useExpiryCountdown';
import { createContext, useContext } from 'react';

export interface BookingExpiryContextValue {
  countdown: CountdownTime | null;
  isExpiring: boolean;
  isExpired: boolean;
  dismissExpiringWarning: () => void;
  // In-progress booking cart state, centralised here so the rehydrate/purge
  // side effect runs once. `inProgressCountdown` is booking-based (the cart),
  // distinct from `countdown` which is exchange-aware (the expiry modals).
  bookingId: string | undefined;
  isValidating: boolean;
  inProgressCountdown: CountdownTime | null;
  handleCheckoutClick: (onBeforeNavigate?: () => void) => void;
}

export const BookingExpiryContext =
  createContext<BookingExpiryContextValue | null>(null);

export function useBookingExpiry(): BookingExpiryContextValue {
  const context = useContext(BookingExpiryContext);
  if (!context) {
    throw new Error(
      'useBookingExpiry must be used within BookingExpiryProvider'
    );
  }
  return context;
}

/**
 * Read-only view of the in-progress booking for the cart / nav badge. The
 * underlying rehydrate/purge side effect is owned by `BookingExpiryProvider`
 * (see `useInProgressBookingController`); consumers only read state here.
 */
export function useInProgressBooking(): {
  bookingId: string | undefined;
  isValidating: boolean;
  countdown: CountdownTime | null;
  handleCheckoutClick: (onBeforeNavigate?: () => void) => void;
} {
  const { bookingId, isValidating, inProgressCountdown, handleCheckoutClick } =
    useBookingExpiry();
  return {
    bookingId,
    isValidating,
    countdown: inProgressCountdown,
    handleCheckoutClick,
  };
}
